/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { Button, Spacer, theme } from "@nordcloud/gnui";
import {
  CloudAccountCloudProvider,
  ResourcesSortFields,
  SortOrder,
  useResourcesSearchBasicQuery,
} from "~/generated/graphql";
import { Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { dateFormat } from "~/constants";
import { useDisclosure, useQueryState } from "~/hooks";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
} from "~/tools";
import { resourcesExcludeFilterMap } from "~/tools/filterStateMaps";
import { ResourceUpdateStatus } from "~/views/resources/ResourceUpdateStatus/types";
import { ResourceQuery } from "~/views/resources/types";
import { prepareFilter } from "../../utils";
import { ResourceDetailsFilters } from "./ResourceDetailsFilters";
import { ResourceOriginFilters } from "./ResourceOriginFilters";
import { ResourceStatusFilters } from "./ResourceStatusFilters";
import { State } from "./types";

type SidebarProps = {
  provider?: CloudAccountCloudProvider;
  resultsCount?: number;
};
type ResourceQueryState = ResourceQuery & {
  page?: string | number;
  limit?: string | number;
};

function buildState(queryState: ResourceQuery) {
  return {
    dateAddedStart: queryState.dateAddedStartUTC
      ? dayjs(queryState.dateAddedStartUTC).format(dateFormat.shortDate)
      : undefined,
    dateAddedEnd: queryState.dateAddedEndUTC
      ? dayjs(queryState.dateAddedEndUTC).format(dateFormat.shortDate)
      : undefined,
    cloudProviders: queryState.cloudProviders,
    excludeCloudProviders: queryState.excludeCloudProviders,
    operatingSystems: queryState.operatingSystems,
    operatingSystemSubTypes: queryState.operatingSystemSubTypes,
    excludeOperatingSystems: queryState.excludeOperatingSystems,
    regions: queryState.regions,
    excludeRegions: queryState.excludeRegions,
    resourceTagKeysAndTagValues: queryState.resourceTagKeysAndTagValues,
    excludeResourceTagKeysAndTagValues:
      queryState.excludeResourceTagKeysAndTagValues,
    cloudAccounts: queryState.cloudAccounts,
    excludeCloudAccounts: queryState.excludeCloudAccounts,
    resourceGroups: queryState.resourceGroups,
    excludeResourceGroups: queryState.excludeResourceGroups,
    plans: queryState.plans,
    excludePlans: queryState.excludePlans,
    resourcePingStatuses: queryState.resourcePingStatuses,
    excludeResourcePingStatuses: queryState.excludeResourcePingStatuses,
    lastUpdateStatuses: queryState.updateStatus,
    excludeLastUpdateStatuses: queryState.excludeUpdateStatus,
  };
}

function buildQueryStateFromSidebarState(
  queryState: ResourceQueryState,
  state: State
): ResourceQueryState {
  return {
    ...queryState,
    dateAddedStartUTC: state.dateAddedStart
      ? dayjs(state.dateAddedStart).format(dateFormat.fullDate)
      : undefined,
    dateAddedEndUTC: state.dateAddedEnd
      ? dayjs(state.dateAddedEnd).endOf("day").format(dateFormat.fullDate)
      : undefined,
    cloudProviders: state.cloudProviders,
    excludeCloudProviders: state.excludeCloudProviders,
    operatingSystems: state.operatingSystems,
    operatingSystemSubTypes: state.operatingSystemSubTypes,
    excludeOperatingSystems: state.excludeOperatingSystems,
    regions: state.regions,
    excludeRegions: state.excludeRegions,
    resourceTagKeysAndTagValues: state.resourceTagKeysAndTagValues,
    excludeResourceTagKeysAndTagValues:
      state.excludeResourceTagKeysAndTagValues,
    cloudAccounts: state.cloudAccounts,
    excludeCloudAccounts: state.excludeCloudAccounts,
    resourceGroups: state.resourceGroups,
    excludeResourceGroups: state.excludeResourceGroups,
    plans: state.plans,
    excludePlans: state.excludePlans,
    resourcePingStatuses: state.resourcePingStatuses,
    excludeResourcePingStatuses: state.excludeResourcePingStatuses,
    updateStatus: state.lastUpdateStatuses as ResourceUpdateStatus[],
    excludeUpdateStatus: state.excludeLastUpdateStatuses,
  };
}

export function ResourcesFiltersSidebar({
  provider,
  resultsCount = 0,
}: SidebarProps) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } = useQueryState<ResourceQuery>();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [isOpen, queryState]);

  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    resourcesExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );

  const { data: previewData, loading: previewLoading } =
    useResourcesSearchBasicQuery({
      variables: {
        page: Number(draftQueryState.page ?? "1"),
        limit: Number(draftQueryState.limit ?? "20"),
        sort: {
          field: draftQueryState.field ?? ResourcesSortFields.Name,
          order: draftQueryState.order ?? SortOrder.Asc,
        },
        input: {
          phrase: isNotEmpty(draftQueryState.searchPhrase?.trim() || "")
            ? draftQueryState.searchPhrase
            : undefined,
          filter: prepareFilter(draftQueryState),
        },
      },
      skip: !isOpen || !hasPendingChanges,
    });

  const previewResultsCount = previewData?.resourcesSearch?.pageInfo?.count;
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading,
    previewResultsCount,
    appliedResultsCount: resultsCount,
    entityLabel: "resources",
  });

  const applyFilters = () => {
    const nextQueryState = buildQueryStateFromSidebarState(queryState, state);

    updateQueryState({
      ...nextQueryState,
      limit: String(nextQueryState.limit ?? queryState.limit ?? "20"),
      page: "1",
    });

    close();
  };
  const clearAllFilters = () => {
    updateState({
      dateAddedStart: undefined,
      dateAddedEnd: undefined,
      cloudProviders: undefined,
      excludeCloudProviders: undefined,
      operatingSystems: undefined,
      operatingSystemSubTypes: undefined,
      excludeOperatingSystems: undefined,
      regions: undefined,
      excludeRegions: undefined,
      resourceTagKeysAndTagValues: undefined,
      excludeResourceTagKeysAndTagValues: undefined,
      cloudAccounts: undefined,
      excludeCloudAccounts: undefined,
      resourceGroups: undefined,
      excludeResourceGroups: undefined,
      plans: undefined,
      excludePlans: undefined,
      resourcePingStatuses: undefined,
      excludeResourcePingStatuses: undefined,
      lastUpdateStatuses: undefined,
      excludeLastUpdateStatuses: undefined,
    });
    updateQueryState({
      ...queryState,
      dateAddedStartUTC: undefined,
      dateAddedEndUTC: undefined,
      cloudProviders: undefined,
      excludeCloudProviders: undefined,
      operatingSystems: undefined,
      operatingSystemSubTypes: undefined,
      excludeOperatingSystems: undefined,
      regions: undefined,
      excludeRegions: undefined,
      resourceTagKeysAndTagValues: undefined,
      excludeResourceTagKeysAndTagValues: undefined,
      cloudAccounts: undefined,
      excludeCloudAccounts: undefined,
      resourceGroups: undefined,
      excludeResourceGroups: undefined,
      plans: undefined,
      excludePlans: undefined,
      resourcePingStatuses: undefined,
      excludeResourcePingStatuses: undefined,
      updateStatus: undefined,
      excludeUpdateStatus: undefined,
      // reset page
      page: "1",
    });
  };

  const filterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "resources",
    excludeFilterMap: resourcesExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? filterSidebarState.helperText;

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <ResourceDetailsFilters
            state={state}
            updateState={updateState}
            provider={provider}
          />
          <Spacer margin={"0 0 1rem 0"} />
          <ResourceOriginFilters
            state={state}
            updateState={updateState}
            provider={provider}
          />
          <Spacer margin={"0 0 1rem 0"} />
          <ResourceStatusFilters state={state} updateState={updateState} />
        </Sidebar>
      </When>
    </>
  );
}
